// ABOUTME: Typed emitters for the paywall → checkout funnel, reported by the surface that owns them.
// ABOUTME: A per-wall instance id makes "exactly once per user action" assertable across re-renders.

import { trackEvent } from './analytics'

export type PaywallSurface =
  | 'tool'
  | 'viral_clips'
  | 'transcript'
  | 'clip_editor'
  | 'configure'
  | 'library'

/**
 * How a wall was closed. `escape` is the keyboard close Radix reports; `back` is a real
 * browser back navigation, which Radix never sees and the modal instruments separately.
 */
export type DismissMethod = 'x' | 'outside_click' | 'escape' | 'back'

export type CheckoutProvider = 'autumn' | 'stripe'

/** Properties every paywall event carries, so one funnel can be sliced by origin. */
interface PaywallContext {
  trigger: string
  tool_slug: string
  surface: PaywallSurface
  paywall_instance_id: string
}

/**
 * Mint the identity of one wall. Created when the wall opens, carried by every event
 * that wall produces, so a re-render cannot inflate the count of walls shown.
 */
export function newPaywallInstanceId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `pw_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`
}

// Instances that have already reported a render. The guard lives here rather than in the
// component so every call site inherits it, and so the unit check can exercise it directly.
const shownInstances = new Set<string>()

/** Forget an instance's guard. Exists for the unit check; a wall id is never reused in the app. */
export function resetPaywallInstance(instanceId: string): void {
  shownInstances.delete(instanceId)
}

/**
 * The offer actually rendered to the user.
 *
 * `price_shown` is derived from real state, so the "Pricing temporarily unavailable"
 * branch is recorded as a failed offer rather than counted as a successful one.
 * Fires at most once per instance: React re-renders and re-opens of the same wall are ignored.
 */
export function trackPaywallModalShown(
  ctx: PaywallContext & { credit_type: string; price_shown: boolean },
): void {
  if (shownInstances.has(ctx.paywall_instance_id)) return
  shownInstances.add(ctx.paywall_instance_id)
  trackEvent('paywall_modal_shown', { ...ctx })
}

/** The user activated the primary upgrade control. */
export function trackPaywallCtaClicked(
  ctx: PaywallContext & { cta_label: string; destination: string; plan_id: string; billing_period: string },
): void {
  trackEvent('paywall_cta_clicked', { ...ctx })
}

/**
 * The user closed the wall without activating it.
 *
 * Also retires the instance's render guard: the wall is gone, its id is never reused, and
 * holding it would grow `shownInstances` without bound across a long session.
 */
export function trackPaywallDismissed(ctx: PaywallContext & { method: DismissMethod }): void {
  trackEvent('paywall_dismissed', { ...ctx })
  shownInstances.delete(ctx.paywall_instance_id)
}

/** The app is about to hand the user off to the off-origin checkout surface. */
export function trackCheckoutRedirectInitiated(
  ctx: PaywallContext & { provider: CheckoutProvider; plan_id: string },
): void {
  trackEvent('checkout_redirect_initiated', { ...ctx })
}

/**
 * The checkout return surface loaded.
 *
 * This is a proxy, not a true "checkout page loaded": the checkout itself is off-origin, so
 * this fires on the return leg. An abandoned off-origin page never emits it.
 */
export function trackCheckoutOpened(ctx: { provider: CheckoutProvider; plan_id: string }): void {
  trackEvent('checkout_opened', { ...ctx })
}
